"use client"

import posthog from "posthog-js"
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"
import { Suspense, useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { HoverTracking } from "@/ui/hoverTracking"

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const posthogUiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ??
      "https://us.posthog.com"

    if (!posthogKey) return

    posthog.init(posthogKey, {
      api_host: "/ingest",
      ui_host: posthogUiHost,
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      disable_session_recording: false,
      enable_recording_console_log: true,
      session_recording: {
        maskAllInputs: true,
        recordHeaders: false,
        recordBody: false,
      },
    })
  }, [])

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      <HoverTracking />
      {children}
    </PHProvider>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      if (searchParams?.toString()) {
        url = url + "?" + searchParams.toString()
      }

      posthog.capture("$pageview", { "$current_url": url })
    }
  }, [pathname, searchParams, posthog])

  return null
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
